import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { OrganizationProvider } from "./context/OrganizationContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "sonner";
import { installAuthInterceptors } from "./lib/axiosAuth";
import { captureUtmsFromUrl } from "./lib/utmTracker";

// Run UTM capture once at app boot so the very first landing — even
// before the user opens the registration form — is attributed.
captureUtmsFromUrl();

// Install global axios interceptors once (Safari ITP fallback via Bearer token).
installAuthInterceptors();

// Regular pages
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";
import Register from "./pages/Register";
import RegisterOrganization from "./pages/RegisterOrganization";
import RegisterPlan from "./pages/RegisterPlan";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import NewCustomerPhased from "./pages/NewCustomerPhased";
import CustomerDetail from "./pages/CustomerDetail";
import Operations from "./pages/Operations";
import AuditLogs from "./pages/AuditLogs";
import Privacy from "./pages/Privacy";
import Reports from "./pages/Reports";
import UserManagement from "./pages/UserManagement";
import MFASettings from "./pages/MFASettings";
import Screening from "./pages/Screening";
import AmlReports from "./pages/AmlReports";
import OperationDetail from "./pages/OperationDetail";
import OrganizationSettings from "./pages/OrganizationSettings";
import Profile from "./pages/Profile";
import PaymentSuccess from "./pages/PaymentSuccess";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ForcePasswordChange from "./pages/ForcePasswordChange";

// SuperAdmin pages
import SuperAdminLayout from "./components/SuperAdminLayout";
import SuperAdminDashboard from "./pages/superadmin/Dashboard";
import SuperAdminOrganizations from "./pages/superadmin/Organizations";
import SuperAdminUsers from "./pages/superadmin/Users";
import SuperAdminCoupons from "./pages/superadmin/Coupons";
import SuperAdminSystemStatus from "./pages/superadmin/SystemStatus";

// SuperAdmin route wrapper
function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-400"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role !== "superadmin") {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <SuperAdminLayout>{children}</SuperAdminLayout>;
}

function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();
  
  // Check URL fragment for session_id synchronously before rendering routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Redirect superadmin to superadmin dashboard unless they chose to view the normal app
  if (!loading && user && user.role === "superadmin" && !location.pathname.startsWith("/superadmin")) {
    const viewingAsUser = sessionStorage.getItem("notalia_admin_viewing_app") === "true";
    if (!viewingAsUser) {
      return <Navigate to="/superadmin" replace />;
    }
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Force-change-password is reachable only when authenticated AND
          the user has must_change_password=true. The page itself does
          its own guard / redirect; we don't wrap in ProtectedRoute to
          avoid the "redirect to dashboard which redirects back" loop. */}
      <Route path="/cambiar-password-obligatorio" element={<ForcePasswordChange />} />
      <Route path="/register" element={<Register />} />
      <Route path="/register/organization" element={<RegisterOrganization />} />
      <Route path="/register/plan" element={<RegisterPlan />} />
      
      {/* SuperAdmin routes */}
      <Route path="/superadmin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
      <Route path="/superadmin/organizations" element={<SuperAdminRoute><SuperAdminOrganizations /></SuperAdminRoute>} />
      <Route path="/superadmin/users" element={<SuperAdminRoute><SuperAdminUsers /></SuperAdminRoute>} />
      <Route path="/superadmin/coupons" element={<SuperAdminRoute><SuperAdminCoupons /></SuperAdminRoute>} />
      <Route path="/superadmin/system-status" element={<SuperAdminRoute><SuperAdminSystemStatus /></SuperAdminRoute>} />
      
      {/* Regular user routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/customers/new" element={<ProtectedRoute><NewCustomerPhased /></ProtectedRoute>} />
      <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
      <Route path="/operations" element={<ProtectedRoute><Operations /></ProtectedRoute>} />
      <Route path="/privacy" element={<ProtectedRoute><Privacy /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/screening" element={<ProtectedRoute><Screening /></ProtectedRoute>} />
      <Route path="/aml-reports" element={<ProtectedRoute><AmlReports /></ProtectedRoute>} />
      <Route path="/operations/:operationId" element={<ProtectedRoute><OperationDetail /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
      <Route path="/mfa" element={<ProtectedRoute><MFASettings /></ProtectedRoute>} />
      <Route path="/audit-logs" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
      <Route path="/organization-settings" element={<ProtectedRoute><OrganizationSettings /></ProtectedRoute>} />
      <Route path="/perfil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OrganizationProvider>
          <AppRouter />
          <Toaster
            position="top-right"
            closeButton={true}
            richColors={true}
            duration={5000}
            toastOptions={{
              style: { background: "#1e293b", color: "#e2e8f0", border: "1px solid #334155" },
            }}
          />
        </OrganizationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
